import { useDailyVisit } from './useDailyVisit'

/**
 * True while a call is live *or* being placed — the window in which abandoning the tab abandons the
 * patient. This is the condition the tab-close warning arms on (QMD-4225), shared by both visit
 * surfaces so they cannot drift apart.
 *
 * Broader than `useCallIsActive`, which is only the fully-connected state:
 *
 * | State                                                  | Included | What the provider sees      |
 * | ------------------------------------------------------ | -------- | --------------------------- |
 * | `PROVIDER_CONNECTING`                                  | yes      | "Calling <name>"            |
 * | `PROVIDER_CONNECTED_AND_WAITING_FOR_PATIENT_CONNECTION` | yes      | "Calling <name>"            |
 * | `CALL_IS_ACTIVE`                                       | yes      | the live call               |
 * | `PROVIDER_WAS_CONNECTED_BUT_PATIENT_CONNECTION_FAILED`  | no       | "No answer" / "Call ended"  |
 * | `CONNECTED_CALL_ENDED`                                 | no       | "Call ended" + retry button |
 * | `IDLE`                                                 | no       | no call — prep, async, etc. |
 *
 * The two excluded states are terminal: the call is already over and the UI says so, so a dialog
 * there is pure nuisance. Note that a *degraded* connection is not one of them — `VisitError`'s
 * "connection unstable" banner is driven by `participantConnectionStatuses`, which is orthogonal to
 * the lifecycle state, so the call stays `CALL_IS_ACTIVE` throughout and is covered by the first row.
 */
export function useCallInProgress(): boolean {
  const { is } = useDailyVisit()
  return is('PROVIDER_CONNECTING', 'PROVIDER_CONNECTED_AND_WAITING_FOR_PATIENT_CONNECTION', 'CALL_IS_ACTIVE')
}
